import { inject, injectable } from "inversify";
import type { IDomainService, IEventService } from "@/interfaces/services";
import { TYPES } from "@/types/symbols";
import type { ApiResponse, PaginatedApiResponse } from "@/types/domain";
import {
  ActionListOptions,
  CreateEventActionPayload,
  CreateWorkflowEventPayload,
  EventAction,
  EventLogErrorKind,
  EventListOptions,
  EventLog,
  EventLogListOptions,
  EventLogPhase,
  UpdateEventActionPayload,
  UpdateWorkflowEventPayload,
  WorkflowEvent,
} from "@/types/event";

@injectable()
export class EventService implements IEventService {
  constructor(
    @inject(TYPES.DomainService) private domainService: IDomainService,
  ) {}

  getEvents(
    options?: EventListOptions,
  ): Promise<PaginatedApiResponse<WorkflowEvent>> {
    return this.domainService.getEvents(normalizeEventListOptions(options));
  }

  getEvent(id: string): Promise<ApiResponse<WorkflowEvent>> {
    return this.domainService.getEvent(id);
  }

  createEvent(
    payload: CreateWorkflowEventPayload,
  ): Promise<ApiResponse<WorkflowEvent>> {
    return this.domainService.createEvent(payload);
  }

  updateEvent(
    id: string,
    payload: UpdateWorkflowEventPayload,
  ): Promise<ApiResponse<WorkflowEvent>> {
    return this.domainService.updateEvent(id, payload);
  }

  deleteEvent(id: string): Promise<ApiResponse<void>> {
    return this.domainService.deleteEvent(id);
  }

  getActions(
    options?: ActionListOptions,
  ): Promise<PaginatedApiResponse<EventAction>> {
    return this.domainService.getActions(normalizeActionListOptions(options));
  }

  getAction(id: string): Promise<ApiResponse<EventAction>> {
    return this.domainService.getAction(id);
  }

  createAction(
    payload: CreateEventActionPayload,
  ): Promise<ApiResponse<EventAction>> {
    return this.domainService.createAction(payload);
  }

  updateAction(
    id: string,
    payload: UpdateEventActionPayload,
  ): Promise<ApiResponse<EventAction>> {
    return this.domainService.updateAction(id, payload);
  }

  deleteAction(id: string): Promise<ApiResponse<void>> {
    return this.domainService.deleteAction(id);
  }

  getEventLogs(
    options?: EventLogListOptions,
  ): Promise<PaginatedApiResponse<EventLog>> {
    return this.domainService.getEventLogs(
      normalizeEventLogListOptions(options),
    );
  }

  getEventLog(id: string): Promise<ApiResponse<EventLog>> {
    return this.domainService.getEventLog(id);
  }

  retryEventLog(id: string): Promise<ApiResponse<EventLog>> {
    return this.domainService.retryEventLog(id);
  }
}

export function normalizeEventListOptions(
  options?: EventListOptions,
): EventListOptions {
  return {
    page: options?.page ?? 1,
    pageSize: options?.pageSize ?? 10,
    filter: {
      name: options?.filter?.name?.trim() || undefined,
      isActive: options?.filter?.isActive,
      workflowId: options?.filter?.workflowId || undefined,
    },
    sorter: {
      createdAt: options?.sorter?.createdAt ?? "desc",
    },
  };
}

export function normalizeActionListOptions(
  options?: ActionListOptions,
): ActionListOptions {
  return {
    page: options?.page ?? 1,
    pageSize: options?.pageSize ?? 10,
    filter: {
      name: options?.filter?.name?.trim() || undefined,
      isActive: options?.filter?.isActive,
    },
    sorter: {
      createdAt: options?.sorter?.createdAt ?? "desc",
    },
  };
}

export function normalizeEventLogListOptions(
  options?: EventLogListOptions,
): EventLogListOptions {
  const normalizeErrorKind = (
    errorKind?: EventLogErrorKind,
  ): EventLogErrorKind | undefined => errorKind ?? undefined;
  const normalizePhase = (
    phase?: EventLogPhase,
  ): EventLogPhase | undefined => phase ?? undefined;

  return {
    page: options?.page ?? 1,
    pageSize: options?.pageSize ?? 10,
    filter: {
      q: options?.filter?.q?.trim() || undefined,
      workflowId: options?.filter?.workflowId || undefined,
      applicationSerialNumber:
        options?.filter?.applicationSerialNumber?.trim() || undefined,
      nodeId: options?.filter?.nodeId?.trim() || undefined,
      eventId: options?.filter?.eventId || undefined,
      actionId: options?.filter?.actionId || undefined,
      status: options?.filter?.status,
      errorKind: normalizeErrorKind(options?.filter?.errorKind),
      phase: normalizePhase(options?.filter?.phase),
    },
    sorter: {
      sortBy: options?.sorter?.sortBy ?? "lastAttemptAt",
      sortOrder: options?.sorter?.sortOrder ?? "desc",
    },
  };
}
